// ===========================================
// Recorded Meet & Greet notes, newest first (#255, enquiries Phase 4)
// ===========================================
// Plain text only (React escapes; never dangerouslySetInnerHTML). Append-only: there is no edit.

import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { brandColors, statusColors } from '../../styles/brandColors';
import { formatInSchoolTz } from '../../utils/dateUtils';
import { ENGAGEMENT_LABELS, OUTCOME_LABELS } from '../../utils/meetAndGreetNotes';
import type { MeetAndGreetNote } from '../../api/meetAndGreetNotes.api';

/** The "Notes missing" chip: the meeting happened and no notes are in. Dark text on the warning tint. */
export function NotesMissingChip({ size = 'small' }: { size?: 'small' | 'medium' }) {
  return (
    <Chip
      label="Notes missing"
      size={size}
      sx={{ bgcolor: statusColors.warningBg, color: brandColors.textPrimary, fontWeight: 600 }}
    />
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function MeetAndGreetNotesList({ notes }: { notes: MeetAndGreetNote[] }) {
  const timeZone = useSchoolTimezone();
  return (
    <Stack spacing={1.5} data-testid="mng-notes-list">
      {notes.map((note, index) => (
        <Paper key={note.id} variant="outlined" sx={{ p: 1.5 }} data-testid="mng-note">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Chip
              label={note.attendance === 'ATTENDED' ? 'Attended' : 'No-show'}
              size="small"
              sx={{
                bgcolor: note.attendance === 'ATTENDED' ? brandColors.mint : statusColors.warningBg,
                color: brandColors.textPrimary,
                fontWeight: 600,
              }}
            />
            {note.outcome && <Chip label={OUTCOME_LABELS[note.outcome] ?? note.outcome} size="small" variant="outlined" />}
            {note.engagement && (
              <Chip label={`Engagement: ${ENGAGEMENT_LABELS[note.engagement] ?? note.engagement}`} size="small" variant="outlined" />
            )}
            {index === 0 && notes.length > 1 && <Chip label="Latest" size="small" color="primary" />}
          </Stack>
          <Stack spacing={1}>
            <Field label="Observations" value={note.observations} />
            <Field label="Prior experience" value={note.priorExperience} />
            <Field label="Recommended program" value={note.recommendedProgram} />
            <Field label="Recommended class" value={note.recommendedClass} />
            <Field label="Notes for the team" value={note.notesForTeam} />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {note.performedByName} ·{' '}
            {formatInSchoolTz(note.createdAt, timeZone, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
}
